import { RowData } from './fileParsers'
import { RoomMember } from '@/redux/types/room'
import { Team } from '@/redux/types/team'

export interface ValidationError {
  row: number
  field?: string
  message: string
}

export interface ValidationWarning {
  row: number
  message: string
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
  teamMappings: Record<string, number>
  teamsToCreate: string[]
}

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

function checkDuplicateEmails(
  rows: RowData[],
  existingMembers: RoomMember[]
): ValidationError[] {
  const errors: ValidationError[] = []
  const emailSet = new Set<string>()
  const existingEmails = new Set(
    existingMembers.map((m) => m.member_email.toLowerCase())
  )

  rows.forEach((row, index) => {
    const email = row.email.toLowerCase().trim()
    if (!email) return

    if (existingEmails.has(email)) {
      errors.push({
        row: index + 2,
        field: 'email',
        message: `Email ${row.email} is already invited to this room`,
      })
    }

    if (emailSet.has(email)) {
      errors.push({
        row: index + 2,
        field: 'email',
        message: `Duplicate email found: ${row.email}`,
      })
    } else {
      emailSet.add(email)
    }
  })

  return errors
}

function validateRequiredFields(rows: RowData[]): ValidationError[] {
  const errors: ValidationError[] = []

  rows.forEach((row, index) => {
    const rowNum = index + 2

    if (!row.firstname.trim()) {
      errors.push({
        row: rowNum,
        field: 'firstname',
        message: 'First name is required',
      })
    }

    if (!row.email.trim()) {
      errors.push({
        row: rowNum,
        field: 'email',
        message: 'Email is required',
      })
    } else if (!isValidEmail(row.email)) {
      errors.push({
        row: rowNum,
        field: 'email',
        message: `Invalid email format: ${row.email}`,
      })
    }
  })

  return errors
}

export function resolveTeamAssignments(
  rows: RowData[],
  existingTeams: Team[]
): {
  teamMappings: Record<string, number>
  warnings: ValidationWarning[]
  teamsToCreate: string[]
} {
  const teamMappings: Record<string, number> = {}
  const warnings: ValidationWarning[] = []
  const teamNameToId: Record<string, number> = {}
  const teamsToCreate: Set<string> = new Set()

  existingTeams.forEach((team) => {
    const normalizedName = team.name.toLowerCase().trim()
    teamNameToId[normalizedName] = team.id
  })

  rows.forEach((row, index) => {
    const teamName = row.team.trim()
    if (!teamName) return

    const normalizedName = teamName.toLowerCase()
    const existingTeamId = teamNameToId[normalizedName]

    if (existingTeamId) {
      teamMappings[row.email.toLowerCase().trim()] = existingTeamId
      warnings.push({
        row: index + 2,
        message: `Team "${teamName}" already exists. Student will be assigned to existing team.`,
      })
    } else {
      teamsToCreate.add(teamName)
    }
  })

  return { teamMappings, warnings, teamsToCreate: Array.from(teamsToCreate) }
}

export function validateRosterData(
  rows: RowData[],
  existingMembers: RoomMember[],
  existingTeams: Team[]
): ValidationResult {
  const errors: ValidationError[] = []
  const warnings: ValidationWarning[] = []

  errors.push(...validateRequiredFields(rows))

  errors.push(...checkDuplicateEmails(rows, existingMembers))

  const {
    teamMappings,
    warnings: teamWarnings,
    teamsToCreate,
  } = resolveTeamAssignments(rows, existingTeams)
  warnings.push(...teamWarnings)

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    teamMappings,
    teamsToCreate,
  }
}
